import React, { useLayoutEffect, useRef, useState } from 'react';
import './OurPlatformInternationalSection.css';
import OptimizedImage from './OptimizedImage';
import ActionButton from '../elements/ActionButton/ActionButton';
import marriottInternationalImg from 'url:../../assets-optimized/redesign/ourPlatform/connecting_people.jpg';
import marriottInternationalWebp from 'url:../../assets-optimized/redesign/ourPlatform/connecting_people.webp';
import marriottInternationalAvif from 'url:../../assets-optimized/redesign/ourPlatform/connecting_people.avif';
import marriottInternationalMobileImg from 'url:../../assets-optimized/redesign/ourPlatform/connecting_people_mobile.png';
import marriottInternationalMobileWebp from 'url:../../assets-optimized/redesign/ourPlatform/connecting_people_mobile.webp';
import marriottInternationalMobileAvif from 'url:../../assets-optimized/redesign/ourPlatform/connecting_people_mobile.avif';

const SparkleIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path
      d='M12 3L13.9 8.1L19 10L13.9 11.9L12 17L10.1 11.9L5 10L10.1 8.1L12 3Z'
      fill='currentColor'
    />
  </svg>
);

const R = 16; // corner radius

// Plain rounded-rectangle panel outline.
const buildRectPath = (w, h, r = R) => {
  const rr = Math.min(r, w / 2, h / 2);
  return [
    `M${rr} 0`,
    `H${w - rr}`,
    `C${w - rr / 2} 0 ${w} ${rr / 2} ${w} ${rr}`,
    `V${h - rr}`,
    `C${w} ${h - rr / 2} ${w - rr / 2} ${h} ${w - rr} ${h}`,
    `H${rr}`,
    `C${rr / 2} ${h} 0 ${h - rr / 2} 0 ${h - rr}`,
    `V${rr}`,
    `C0 ${rr / 2} ${rr / 2} 0 ${rr} 0`,
    'Z',
  ].join(' ');
};

// Glass panel modelled on the home hero-badge: the background image is drawn
// into the SVG and blurred with feGaussianBlur (a reliable blur, unlike
// backdrop-filter inside a foreignObject), then clipped to the stepped shape so
// it reads as one piece with no seam.
const TitleGlassPanel = ({ imageSrc, metrics }) => {
  const blurId = React.useId();
  const clipId = React.useId();

  if (!metrics) return null;

  const m = metrics;
  const path = buildRectPath(m.w, m.h);

  return (
    <svg
      className='od-international-title-panel'
      viewBox={`0 0 ${m.w} ${m.h}`}
      preserveAspectRatio='none'
      aria-hidden='true'
      focusable='false'
    >
      <defs>
        <clipPath id={clipId}>
          <path d={path} />
        </clipPath>
        <filter id={blurId} x='-20%' y='-20%' width='140%' height='140%'>
          <feGaussianBlur in='SourceGraphic' stdDeviation='16' />
        </filter>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <image
          href={imageSrc}
          x={-m.offsetX}
          y={-m.offsetY}
          width={m.cardW}
          height={m.cardH}
          preserveAspectRatio='xMidYMid slice'
          filter={`url(#${blurId})`}
        />
        <path d={path} fill='rgba(23,23,23,0.32)' />
      </g>
      <path d={path} fill='none' stroke='rgba(255,255,255,0.35)' strokeWidth='1' />
    </svg>
  );
};

const TITLE_PAD_X = 30;

const OurPlatformInternationalSection = () => {
  const wrapRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const [metrics, setMetrics] = useState(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const card = wrap.closest('.od-international-card');

    const measure = () => {
      if (!wrap || !card || !line1Ref.current || !line2Ref.current) return;
      const wr = wrap.getBoundingClientRect();
      const cr = card.getBoundingClientRect();
      const l1 = line1Ref.current.getBoundingClientRect();
      const l2 = line2Ref.current.getBoundingClientRect();
      if (wr.width === 0 || cr.width === 0) return;
      setMetrics({
        w: wr.width,
        h: wr.height,
        topW: l1.right - wr.left + TITLE_PAD_X,
        // Put the step boundary 30px above the second line so the text under the
        // notch ("...OF TRAVEL.") gets the same padding from the panel edge
        splitH: Math.max(l2.top - wr.top - TITLE_PAD_X, l1.top - wr.top + 8),
        offsetX: wr.left - cr.left,
        offsetY: wr.top - cr.top,
        cardW: cr.width,
        cardH: cr.height,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    if (card) observer.observe(card);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <section className='od-international-section'>
      <div className='od-international-shell'>
        <header className='od-international-header'>
          <h2 className='od-international-title'>Marriott International</h2>

          <p className='od-international-copy'>
            With more than 30 leading brands and nearly 9,600 properties in 143 countries and
            territories, our unique global footprint gives brands more ways to connect with
            travelers while they{' '}
            <span className='od-international-highlight'>
              <span className='od-international-highlight-icon'>
                <SparkleIcon />
              </span>
              experience and expand their world.
            </span>
          </p>
        </header>

        <article className='od-international-card'>
          <OptimizedImage
            className='od-international-card-image'
            avif={marriottInternationalAvif}
            webp={marriottInternationalWebp}
            fallback={marriottInternationalImg}
            mobileAvif={marriottInternationalMobileAvif}
            mobileWebp={marriottInternationalMobileWebp}
            mobileFallback={marriottInternationalMobileImg}
            mobileMedia='(max-width: 767px)'
            alt='Marriott International travel destination'
          />

          <div className='od-international-card-overlay'>
            <div className='od-international-title-wrap' ref={wrapRef}>
              <TitleGlassPanel imageSrc={marriottInternationalWebp} metrics={metrics} />

              <h3 className='od-international-card-title'>
                <span className='od-international-title-line' ref={line1Ref}>
                  Connecting People
                </span>
                <span className='od-international-title-line' ref={line2Ref}>
                  Through the Power of Travel.
                </span>
              </h3>
            </div>

            <div className='od-international-actions'>
              <ActionButton
                href='https://www.marriott.com/marriott/aboutmarriott.mi'
                target='_blank'
                text='About Marriott International'
              />
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};

export default OurPlatformInternationalSection;
